import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Application } from './application';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { CONTEXT_TYPE } from '../core/auth/context-type';
import { PATH } from '../core/const/path';
import { Context } from '../core/auth/context';
import { ContextGroup } from './context-group';
import { ENTITY_TYPE } from '../core/const/entity-type';

@Injectable({
  providedIn: 'root'
})
export class NavbarService {

  private readonly httpClient = inject(HttpClient);

  getApplicationsVersion2(): Observable<Application[]> {
    return this.httpClient.get<Application[]>('/v2/me/applications');
  }

  getContextGroups(list: Context[], msg: { [key: string]: string }): ContextGroup[] {
    const contextGroups: ContextGroup[] = [];

    const customerGroup = this.getContextGroup(list, msg['labelSubscriber'], ENTITY_TYPE.customer,
      [CONTEXT_TYPE.customerOfVisma, CONTEXT_TYPE.customer]);
    if (customerGroup) contextGroups.push(customerGroup);

    const companyGroup = this.getContextGroup(list, msg['labelCompany'], ENTITY_TYPE.company,
      [CONTEXT_TYPE.company]);
    if (companyGroup) contextGroups.push(companyGroup);

    const distributorGroup = this.getContextGroup(list, msg['labelBusinessUnit'], ENTITY_TYPE.distributor,
      [CONTEXT_TYPE.distributor]);
    if (distributorGroup) contextGroups.push(distributorGroup);

    return contextGroups;
  }

  // Grouped by `contextType`, not by the roles the context carries. The roles cannot answer this: a
  // Distributor context carries Administrator alongside its BU roles, so it matched the customer
  // filter as well and the same business unit appeared in two groups at once (seen in the live
  // dropdown). The type says what the context *is*, which is the question the group is asking.
  private getContextGroup(list: Context[], label: string, entityType: string, types: string[]): ContextGroup | null {
    const items = list
      .filter(context => types.includes(context.contextType ?? ''))
      .sort((a, b) => a.name.localeCompare(b.name));
    return items.length > 0 ? new ContextGroup(label, entityType, items) : null;
  }

  getRootContextPath(roles: string[], contextType?: string, taskId?: string): string {
    // A Distributor's root is the distributor area whatever its roles say - an Administrator-only
    // Distributor has no BU role to recognise it by, and the chain below would answer /provisioning.
    if (contextType === CONTEXT_TYPE.distributor) {
      return PATH.context_distributor;
    }
    if (roles.includes(PROFILE_ROLE.buAccessAdministrator) || roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
      return PATH.context_distributor;
    } else if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
      return PATH.context_customer;
    } else if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
      return PATH.context_company;
    } else if (roles.includes(PROFILE_ROLE.administrator)) {
      // An Administrator context with a pending task goes to onboarding; otherwise to provisioning.
      return taskId ? PATH.set_up_your_company : PATH.provisioning;
    }
    return '/forbidden';
  }

}
